import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Avatar,
  Link,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useSurveyDrafts from '@/hooks/useSurveyDrafts';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { PatientDates } from '@/patient-app-common/hooks/usePatientDates';
import { Patient } from '@/patient-app-common/types/Patient';

import { DailyResult } from './DailyResult';

interface HealthCheckCardProps {
  patient: Patient;
  patientDates: PatientDates | null;
}

export function HealthCheckCard({
  patient,
  patientDates,
}: HealthCheckCardProps) {
  const { activeTrackers } = useAppEvents();

  const entryFromToday = useMemo(() => {
    if (!patient || activeTrackers.length) return false;

    const { mainTrackerId, surveys } = patient;

    const lastEntryDate = _.find(surveys, ['id', mainTrackerId])?.lastEntryDate;

    if (lastEntryDate) {
      return dayjs().isSame(lastEntryDate, 'd');
    }
    return false;
  }, [activeTrackers, patient]);

  const hasHealthCheck = useMemo(() => {
    if (!patient || !patientDates || activeTrackers.length) return false;

    const { daysWithCheckIn, daysPostOp } = patient;
    const { dischargeDate, startOfDayInHospitalTz } = patientDates;

    const daysToSurvey = daysWithCheckIn ?? daysPostOp;
    const programEndDate = dayjs(dischargeDate).add(daysToSurvey, 'days');

    const withinDaysToSurvey =
      daysToSurvey > 0 &&
      dayjs(startOfDayInHospitalTz).isBefore(programEndDate);

    return withinDaysToSurvey;
  }, [activeTrackers, patient, patientDates]);

  if (!patient) return null;

  if (entryFromToday) {
    return <DailyResult mainTrackerId={patient.mainTrackerId} />;
  }

  if (hasHealthCheck) {
    return <MainTrackerLink mainTrackerId={patient.mainTrackerId} />;
  }

  return null;
}

function MainTrackerLink({ mainTrackerId }: { mainTrackerId: number }) {
  const { t } = useTranslation('home');
  const { surveyDrafts } = useSurveyDrafts();

  const hasDraft = useMemo(() => {
    if (!surveyDrafts?.length) return false;

    const draft = _.find(surveyDrafts, {
      procedureId: mainTrackerId,
    });
    const draftDate = draft?.draftCreatedAt;

    if (!draftDate) return false;

    const draftFromToday = dayjs()
      .startOf('day')
      .isSame(dayjs(draftDate).startOf('day'));

    return draftFromToday;
  }, [mainTrackerId, surveyDrafts]);

  const btnText = hasDraft
    ? t('common.button.survey_edit')
    : t('common.button.survey_start');

  return (
    <Card className="bg-primary-100" radius="sm" shadow="sm">
      <CardHeader className="flex pb-0">
        <Avatar
          color="primary"
          icon={
            <Icon
              icon="material-symbols:health-and-safety-outline"
              className="text-3xl text-background"
            />
          }
          size="md"
        />
        <div className="flex flex-col">
          <h2 className="font-semibold px-8">
            {t('common.text.main_tracker')}
          </h2>
          <p className="px-8">{t('healthcheckcard.description')}</p>
        </div>
      </CardHeader>
      <CardBody className="pt-0">
        <Button
          as={Link}
          href={`/tracker/${mainTrackerId}`}
          className="mt-4 min-w-1/4 place-self-end"
          color="primary"
        >
          {btnText}
        </Button>
        {hasDraft && (
          <div className="text-right font-light">
            <p className="inline-flex text-sm gap-1 text-right">
              <Icon icon="mdi:pencil" className="place-self-center" />
              <span>{t('common.text.has_draft')}</span>
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
